import React, { useEffect, useRef, useState } from 'react'
import { fetchPublic } from '../ticketFetch'

const BP_REMOVED_BY_OPTIONS = [
  'QIS BLE', 'SBD Tool', 'No Swapped', 'Drill Tool', 'Testing', 'SBA Tool', 'NFC Device',
]

const BP_CHECKED_AT_OPTIONS = [
  'In Client Hub', 'In SMPL Hub', 'At QIS', 'On Road',
]

const ACTUAL_FAULT_OPTIONS = [
  'All Charge Flags are not set',
  'Battery Drop',
  'Battery Error',
  'Battery Orphan',
  'Boot Switch Error',
  'Boot Switch shorted',
  'BP Addressing Failed in Vehicle',
  'BP Damaged',
  'BP Degradation High',
  'BP Error/ Vehicle not running with 100% SOC BP',
  'BP LED Not Working',
  'BP Quarantine',
  'BP Sound Coming',
  'BP Warning',
  'Capacity Due',
  'Capacity Warning',
  'Charge mode AC power OFF',
  'Connecting Issue - No Error code on SBA/SBD Screen',
  'DIU 12V Output',
  'Dock Damaged',
  'Dock ID Mismatched',
  'Dock Side Wiring Issue',
  'Dock Temperature Issue',
  'DV Drive',
  'EV Out Sense Malfunction',
  'Harness issue repair',
  'Invalid Location (GPS)',
  'Lock sense fault',
  'Vehicle Key Fob Not Mapped',
  'Vehicle Side Wiring Issue',
  'Low SOC',
  'MCU Communication',
  'Min cell voltage & DV Drive',
  'MOSFAT Malfunctions',
  'Mosfet Failure',
  'Multiple Times Key fob Tapping - Swap Done',
  'No Error code on SBA Screen',
  'Over Voltage',
  'PCB Membrane Damaged',
  'Power on Reset',
  'Precharge',
  'Ready to Swap',
  'Recover',
  'Removed by admin key fob',
  'Resolved Remotely by ERT',
  'Rider Go back to Hub/ Home',
  'Rider Not Contactable',
  'Self-Resolved',
  'Short Temperature',
  'Solenoid Fault',
  'State of Charge',
  'Stop BLE',
  'TBD',
  'Test Ticket',
  'TIU Communication Error',
  'Top Cover Broken',
  'TOTP Mismatch',
  'Unauthorized Unlock',
  'Under Voltage',
  'Visited & resolved by ERT',
  'Water Inside in Dock',
]

const TICKET_STATUS_OPTIONS = [
  { value: 'on_hold', label: 'On Hold' },
  { value: 'closed',  label: 'Closed'  },
]

const emptyForm = (resolvedData) => {
  const rf = resolvedData?.resolution_fields || {}
  return {
    bp_id:           rf.bp_id           || '',
    bp_removed_by:   rf.bp_removed_by   || '',
    bp_checked_at:   rf.bp_checked_at   || '',
    actual_fault:    rf.actual_fault    || '',
    bp_soc_percent:  rf.bp_soc_percent  || '',
    remarks:         rf.remarks         || '',
    ticket_status:   resolvedData?.ticket_status  || '',
    issue_resolved:  resolvedData?.issue_resolved || '',
  }
}

export default function ErtCloseForm({ ticketId, resolvedData, readOnly, onSubmit, submitting }) {
  const [form, setForm]               = useState(() => emptyForm(resolvedData))
  const [errors, setErrors]           = useState({})
  const [imageFiles, setImageFiles]   = useState([])
  const [existingImages, setExistingImages] = useState([])
  const [imageLoading, setImageLoading]     = useState(false)
  const [imageUploading, setImageUploading] = useState(false)
  const fileInputRef                  = useRef(null)

  const isClosed = form.ticket_status === 'closed'

  useEffect(() => {
    if (!ticketId) return
    setImageLoading(true)
    fetchPublic(`/api/tickets/ticket/get-attachments/${ticketId}/?attachment_type=sbd_fault_screen`)
      .then((data) => {
        const list = Array.isArray(data) ? data : []
        setExistingImages(list.map((a) => ({ url: a.file, name: a.file_name?.split('/').pop() || 'sbd-image' })))
      })
      .catch(() => {})
      .finally(() => setImageLoading(false))
  }, [ticketId])

  function set(field, value) {
    setForm((prev) => {
      const next = { ...prev, [field]: value }
      if (field === 'ticket_status' && value !== 'closed') next.issue_resolved = ''
      return next
    })
    setErrors((prev) => ({ ...prev, [field]: '' }))
  }

  function handleImageSelect(e) {
    const selected = Array.from(e.target.files || [])
    if (!selected.length) return
    setImageFiles((prev) => [...prev, ...selected.map((file) => ({ file, preview: URL.createObjectURL(file) }))])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  async function uploadImages(files) {
    if (!files.length || !ticketId) return
    setImageUploading(true)
    try {
      const fd = new FormData()
      files.forEach(({ file }) => fd.append('files', file))
      await fetchPublic(
        `/api/tickets/ticket/add-attachments/${ticketId}/?attachment_type=sbd_fault_screen`,
        { method: 'POST', body: fd }
      )
      setExistingImages((prev) => [...prev, ...files.map(({ file, preview }) => ({ url: preview, name: file.name }))])
      setImageFiles([])
    } catch {
      setErrors((prev) => ({ ...prev, images: 'Upload failed. Please retry.' }))
      throw new Error('upload_failed')
    } finally {
      setImageUploading(false)
    }
  }

  function validate() {
    const e = {}
    if (!form.ticket_status)              e.ticket_status  = 'Required'
    if (isClosed && !form.issue_resolved) e.issue_resolved = 'Required'
    return e
  }

  async function handleSubmit(e) {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length) { setErrors(errs); return }

    if (imageFiles.length) {
      try { await uploadImages(imageFiles) } catch { return }
    }

    onSubmit({
      resolution_fields: {
        bp_id:          form.bp_id,
        bp_removed_by:  form.bp_removed_by,
        bp_checked_at:  form.bp_checked_at,
        actual_fault:   form.actual_fault,
        bp_soc_percent: form.bp_soc_percent,
        remarks:        form.remarks,
      },
      ticket_status:   form.ticket_status,
      attachment_type: 'sbd_fault_screen',
      ...(isClosed && { issue_resolved: form.issue_resolved }),
    })
  }

  return (
    <form className="ctf-section" onSubmit={handleSubmit} noValidate>

      {readOnly && (
        <div className="ctf-readonly-banner">
          &#128274; This ticket is closed — view only
        </div>
      )}

      {/* BP ID */}
      <Field label="BP ID" error={errors.bp_id}>
        <input
          className="ctf-input"
          value={form.bp_id}
          onChange={(e) => set('bp_id', e.target.value)}
          placeholder="Enter BP ID"
          readOnly={readOnly}
        />
      </Field>

      {/* BP Removed By */}
      <Field label="BP Removed By" error={errors.bp_removed_by}>
        <select
          className="ctf-select"
          value={form.bp_removed_by}
          onChange={(e) => set('bp_removed_by', e.target.value)}
          disabled={readOnly}
        >
          <option value="">Select option</option>
          {BP_REMOVED_BY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </Field>

      {/* BP Checked At */}
      <Field label="BP Checked At" error={errors.bp_checked_at}>
        <select
          className="ctf-select"
          value={form.bp_checked_at}
          onChange={(e) => set('bp_checked_at', e.target.value)}
          disabled={readOnly}
        >
          <option value="">Select location</option>
          {BP_CHECKED_AT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </Field>

      {/* Actual Fault Found */}
      <Field label="Actual Fault Found" error={errors.actual_fault}>
        <select
          className="ctf-select"
          value={form.actual_fault}
          onChange={(e) => set('actual_fault', e.target.value)}
          disabled={readOnly}
        >
          <option value="">Select fault</option>
          {ACTUAL_FAULT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </Field>

      {/* BP SOC % */}
      <Field label="BP SOC %" error={errors.bp_soc_percent}>
        <input
          className="ctf-input"
          type="number"
          min="0"
          max="100"
          value={form.bp_soc_percent}
          onChange={(e) => set('bp_soc_percent', e.target.value)}
          placeholder="e.g. 85"
          readOnly={readOnly}
        />
      </Field>

      {/* Attached Images of SBD Fault Screen */}
      <Field label="Attached Images of SBD Fault Screen" error={errors.images}>
        {imageLoading ? (
          <p className="ctf-hint">Loading…</p>
        ) : (
          <div className="ctf-image-preview-wrap">
            {existingImages.map((img, i) => (
              <div key={i} className="ctf-image-item">
                <img src={img.url} alt={img.name} className="ctf-image-preview" />
                <p className="ctf-hint">{img.name}</p>
              </div>
            ))}
            {imageFiles.map((entry, i) => (
              <div key={i} className="ctf-image-item">
                <img src={entry.preview} alt={`new-${i}`} className="ctf-image-preview" />
                <div className="ctf-image-actions">
                  <span className="ctf-hint">{entry.file.name}</span>
                  {!readOnly && (
                    <button type="button" className="ctf-image-remove" onClick={() => setImageFiles((prev) => prev.filter((_, idx) => idx !== i))}>
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))}
            {imageFiles.length > 0 && !readOnly && (
              <button type="button" className="ctf-upload-btn" onClick={() => uploadImages(imageFiles)} disabled={imageUploading}>
                {imageUploading ? 'Uploading…' : `↑ Upload ${imageFiles.length} photo${imageFiles.length > 1 ? 's' : ''}`}
              </button>
            )}
            {!readOnly && (
              <label className="ctf-file-label" style={existingImages.length || imageFiles.length ? { marginTop: 4 } : {}}>
                <input ref={fileInputRef} type="file" accept="image/*" multiple capture="environment" className="ctf-file-input" onChange={handleImageSelect} />
                <span className="ctf-file-btn">
                  {existingImages.length || imageFiles.length ? '&#128247; Add more photos' : '&#128247; Upload photos'}
                </span>
              </label>
            )}
            {!existingImages.length && !imageFiles.length && readOnly && (
              <p className="ctf-hint">No photos uploaded</p>
            )}
          </div>
        )}
      </Field>

      {/* Remarks */}
      <Field label="Remarks" error={errors.remarks}>
        <textarea
          className="ctf-textarea"
          value={form.remarks}
          onChange={(e) => set('remarks', e.target.value)}
          placeholder="Add remarks…"
          readOnly={readOnly}
        />
      </Field>

      <div className="ctf-divider" />

      {/* Ticket Status */}
      <Field label="Ticket Status" required={!readOnly} error={errors.ticket_status}>
        <select
          className="ctf-select"
          value={form.ticket_status}
          onChange={(e) => set('ticket_status', e.target.value)}
          disabled={readOnly}
        >
          <option value="">Select status</option>
          {TICKET_STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </Field>

      {/* Issue Resolved */}
      <Field
        label="Issue Resolved"
        required={isClosed && !readOnly}
        error={errors.issue_resolved}
        hint={!isClosed && !readOnly ? 'Available only when Ticket Status is Closed' : undefined}
      >
        <div className={`ctf-radio-group ${(!isClosed || readOnly) ? 'ctf-radio-group--disabled' : ''}`}>
          {['yes', 'no'].map((v) => (
            <label key={v} className="ctf-radio-label">
              <input
                type="radio"
                name="issue_resolved"
                value={v}
                checked={form.issue_resolved === v}
                disabled={!isClosed || readOnly}
                onChange={() => set('issue_resolved', v)}
                className="ctf-radio"
              />
              {v === 'yes' ? 'Yes' : 'No'}
            </label>
          ))}
        </div>
      </Field>

      {!readOnly && (
        <button type="submit" className="ctf-submit-btn" disabled={submitting}>
          {submitting ? 'Submitting…' : 'Update Ticket'}
        </button>
      )}
    </form>
  )
}

function Field({ label, required, error, hint, children }) {
  return (
    <div className="ctf-field">
      <span className="ctf-label">
        {label}
        {required && <span className="ctf-required">*</span>}
      </span>
      {children}
      {hint  && !error && <span className="ctf-hint">{hint}</span>}
      {error && <span className="ctf-field-error">{error}</span>}
    </div>
  )
}
